/**
 * Анонимная анкета (quiz-funnel, 2026-09) — состояние в браузере.
 *
 * Браузер без Telegram и без email-сессии проходит анкету анонимно:
 *  - session_id (uuid4) + ответы + номер шага лежат в localStorage (мгновенное
 *    восстановление) и дублируются на сервер (POST /api/quiz/session) — сервер
 *    нужен, чтобы анкета «доехала» до аккаунта, даже если ссылку из письма
 *    открыли в другом браузере;
 *  - plan (?plan=pdf|sub) и UTM/yclid — первое касание, живут в той же сессии.
 *
 * Внутри Telegram всё это НЕ используется: анкета работает по-старому.
 */

import type { OnboardingRestPayload } from '../services/api';
import { computeNutrition } from './nutrition';

/** Собственный реф-код АИши для PDF без специалиста. Зеркало config.py::pdf_house_ref_code. */
export const PDF_HOUSE_REF_CODE = 'AISHA';

const SESSION_ID_KEY = 'aisha_quiz_session_id';
const STATE_KEY = 'aisha_quiz_state';
const META_KEY = 'aisha_quiz_meta';

export type QuizPlan = 'pdf' | 'sub';

export interface QuizAnswers {
  age?: number;
  gender?: 'male' | 'female';
  height?: number;
  weight?: number;
  goal?: 'weight_loss' | 'maintenance' | 'muscle_gain' | 'medical';
  activityLevel?: 'minimal' | 'low' | 'medium' | 'high' | 'very_high';
  extras?: string;
  medicalConditions?: string[];
  allergies?: string[];
  targetWeight?: number;
  medications?: string[];
  pdpConsent?: boolean;
  healthDataConsent?: boolean;
}

export interface QuizResult {
  calories: number;
  protein: number;
  fat: number;
  carbs: number;
  tdee: number;
  bmi: number;
}

export interface QuizState {
  stepIdx: number;
  answers: QuizAnswers;
  result?: QuizResult | null;
}

export interface QuizMeta {
  plan?: QuizPlan;
  utm?: Record<string, string>;
}

const UTM_KEYS = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_term', 'utm_content', 'yclid'] as const;

function readJson<T>(key: string): T | null {
  try {
    const raw = localStorage.getItem(key);
    return raw ? (JSON.parse(raw) as T) : null;
  } catch {
    return null;
  }
}

function writeJson(key: string, value: unknown): void {
  try {
    localStorage.setItem(key, JSON.stringify(value));
  } catch {
    /* приватный режим — работаем без кэша */
  }
}

/** Внутри Telegram Mini App (есть подписанный initData). */
export function isInsideTelegram(): boolean {
  return !!window.Telegram?.WebApp?.initData;
}

/**
 * Страница открыта клиентом Telegram, даже если initData ещё не пришёл.
 * telegram-web-app.js в обычном браузере даёт platform = 'unknown',
 * а внутри Telegram — 'android' / 'ios' / 'tdesktop' / 'weba' и т.п.
 */
export function isTelegramShell(): boolean {
  const platform = (window.Telegram?.WebApp as { platform?: string } | undefined)?.platform;
  return isInsideTelegram() || (!!platform && platform !== 'unknown');
}

/** Браузер без входа: анкета идёт анонимно. */
export function isAnonymousBrowser(): boolean {
  let hasSession = false;
  try {
    hasSession = !!localStorage.getItem('aisha_session_key');
  } catch {
    hasSession = false;
  }
  return !isTelegramShell() && !hasSession;
}

function uuid4(): string {
  const c = (window as unknown as { crypto?: Crypto }).crypto;
  if (c && typeof (c as Crypto & { randomUUID?: () => string }).randomUUID === 'function') {
    return (c as Crypto & { randomUUID: () => string }).randomUUID();
  }
  const bytes = new Uint8Array(16);
  if (c?.getRandomValues) {
    c.getRandomValues(bytes);
  } else {
    for (let i = 0; i < 16; i++) bytes[i] = Math.floor(Math.random() * 256);
  }
  bytes[6] = (bytes[6] & 0x0f) | 0x40;
  bytes[8] = (bytes[8] & 0x3f) | 0x80;
  const hex = Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('');
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
}

export function getQuizSessionId(): string | null {
  try {
    return localStorage.getItem(SESSION_ID_KEY);
  } catch {
    return null;
  }
}

export function getOrCreateQuizSessionId(): string {
  const existing = getQuizSessionId();
  if (existing) return existing;
  const id = uuid4();
  try {
    localStorage.setItem(SESSION_ID_KEY, id);
  } catch {
    /* ignore */
  }
  return id;
}

/** Новая сессия (старая уже привязана к аккаунту на сервере). Ответы сохраняются. */
export function renewQuizSessionId(): string {
  try {
    localStorage.removeItem(SESSION_ID_KEY);
  } catch {
    /* ignore */
  }
  return getOrCreateQuizSessionId();
}

export function loadQuizState(): QuizState | null {
  return readJson<QuizState>(STATE_KEY);
}

export function saveQuizState(state: QuizState): void {
  writeJson(STATE_KEY, state);
}

export function loadQuizMeta(): QuizMeta {
  return readJson<QuizMeta>(META_KEY) || {};
}

/** Анкета перенесена в аккаунт — чистим анонимное состояние (meta с plan оставляем до оплаты). */
export function clearQuizState(): void {
  try {
    localStorage.removeItem(STATE_KEY);
    localStorage.removeItem(SESSION_ID_KEY);
  } catch {
    /* ignore */
  }
}

export function clearQuizMeta(): void {
  try {
    localStorage.removeItem(META_KEY);
  } catch {
    /* ignore */
  }
}

/**
 * Читает ?plan= и UTM/yclid из адреса при входе.
 * plan — последнее явное значение в ссылке; UTM — первое касание (не перезаписываем).
 */
export function captureEntryParams(search: string = window.location.search): QuizMeta {
  const meta = loadQuizMeta();
  try {
    const params = new URLSearchParams(search);
    const plan = params.get('plan');
    if (plan === 'pdf' || plan === 'sub') meta.plan = plan;

    if (!meta.utm || Object.keys(meta.utm).length === 0) {
      const utm: Record<string, string> = {};
      UTM_KEYS.forEach((k) => {
        const v = params.get(k);
        if (v) utm[k] = v.slice(0, 255);
      });
      if (Object.keys(utm).length > 0) {
        if (document.referrer) utm.referrer = document.referrer.slice(0, 500);
        utm.landing_page = (window.location.pathname + search).slice(0, 500);
        meta.utm = utm;
      }
    }
  } catch {
    /* ignore */
  }
  writeJson(META_KEY, meta);
  return meta;
}

/** Ответы анкеты → тело POST /api/onboarding (тот же формат, что шлёт OnboardingScreen). */
export function answersToOnboardingPayload(a: QuizAnswers): OnboardingRestPayload | null {
  if (!a.age || !a.gender || !a.height || !a.weight || !a.goal || !a.activityLevel) return null;
  return {
    age: a.age,
    gender: a.gender,
    height: a.height,
    weight: a.weight,
    goal: a.goal,
    activity_level: a.activityLevel,
    health_conditions: a.medicalConditions || [],
    dietary_restrictions: a.allergies || [],
    additional_info: (a.extras || '').trim(),
    target_weight_kg: a.targetWeight,
    medications: a.medications || [],
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    language: 'ru',
    pdp_consent: !!a.pdpConsent,
    health_data_consent: !!a.healthDataConsent,
  };
}

/**
 * После сохранения профиля: кладём в localStorage то же, что кладёт OnboardingScreen,
 * чтобы BriefScreen/кабинет работали как у обычного пользователя.
 */
export function persistProfileCaches(a: QuizAnswers, r: QuizResult): void {
  writeJson('aisha_brief', {
    userId: 0,
    profileCreated: true,
    recommendedCalories: r.calories,
    recommendedMacros: { protein: r.protein, fat: r.fat, carbs: r.carbs },
  });
  writeJson('aisha_onboarding_form', {
    gender: a.gender,
    age: a.age,
    height: a.height,
    weight: a.weight,
    goal: a.goal,
    activityLevel: a.activityLevel,
    additional_info: (a.extras || '').trim(),
    medicalConditions: a.medicalConditions || [],
    allergies: a.allergies || [],
    targetWeight: a.targetWeight,
    medications: a.medications || [],
  });
  try {
    localStorage.setItem('aisha_profile_done', '1');
  } catch {
    /* ignore */
  }
}

/**
 * Запасной расчёт в браузере — зеркало формулы бэка (utils/nutrition.ts = helpers.py).
 * Нужен, если POST /api/quiz/calculate недоступен (сеть/лимит): человек всё равно видит норму.
 */
export function computeLocalResult(a: QuizAnswers): QuizResult | null {
  if (!a.age || !a.gender || !a.height || !a.weight || !a.goal || !a.activityLevel) return null;
  const n = computeNutrition({
    gender: a.gender,
    age: a.age,
    weight: a.weight,
    height: a.height,
    activity: a.activityLevel,
    goal: a.goal,
    targetWeight: a.targetWeight,
  });
  return {
    calories: n.calories,
    protein: n.macros.protein,
    fat: n.macros.fat,
    carbs: n.macros.carbs,
    tdee: n.tdee,
    bmi: +(a.weight / Math.pow(a.height / 100, 2)).toFixed(1),
  };
}
